import { useEffect } from 'react';
import type { Selection } from './types';
import type { GroupEntry } from './layout';

const NOOP = () => {};

interface UseEditKeyboardArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  selection: Selection;
  editMode: boolean;
  layout: GroupEntry[];
  sunX: number; sunY: number; sunR: number;
  onActivate?: (selection: Selection) => void;
}

export function useEditKeyboard({ dispatch, selection, editMode, layout, sunX, sunY, sunR, onActivate = NOOP }: UseEditKeyboardArgs) {
  useEffect(() => {
    if (!editMode || !selection) return;
    function onKeyDown(e: KeyboardEvent) {
      if (document.querySelector('.mo.show')) return;
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement?.tagName || '')) return;
      if (!selection) return;
      const step = e.shiftKey ? 20 : 5;
      if (e.key === 'Escape') { dispatch({ type: 'DESELECT' }); return; }
      if (e.key === 'Enter') { e.preventDefault(); onActivate(selection); return; }
      if (selection.kind === 'person') {
        const person = layout[selection.gi]?.progs[selection.pi]?.people[selection.ki]?.p;
        if (!person) return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          const cur = layout[selection.gi].progs[selection.pi].people[selection.ki].ang;
          const delta = (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 15 : 3);
          dispatch({ type: 'SET_PERSON_ANGLE', gi: selection.gi, pi: selection.pi, ki: selection.ki, ang: cur + delta });
        } else if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          dispatch({ type: 'CYCLE_LABEL_MODE', gi: selection.gi, pi: selection.pi, ki: selection.ki });
        }
        return;
      }
      if (selection.kind === 'sun') {
        if (e.key === '-' || e.key === '=' || e.key === '+') {
          e.preventDefault();
          dispatch({ type: 'SET_SIZE', target: 'sun', value: sunR + (e.key === '-' ? -6 : 6) });
        } else if (e.key.startsWith('Arrow')) {
          e.preventDefault();
          let nx = sunX, ny = sunY;
          if (e.key === 'ArrowLeft') nx -= step; if (e.key === 'ArrowRight') nx += step;
          if (e.key === 'ArrowUp') ny -= step; if (e.key === 'ArrowDown') ny += step;
          dispatch({ type: 'SET_DISC_POSITION', kind: 'sun', x: nx, y: ny });
        }
        return;
      }
      const isGroup = selection.kind === 'group';
      const ge = layout[selection.gi];
      if (!ge) return;
      if (!isGroup && !ge.progs[selection.pi]) return;
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        let nx: number, ny: number;
        if (isGroup) { nx = ge.gcx; ny = ge.gcy; } else { nx = ge.progs[selection.pi].pcx; ny = ge.progs[selection.pi].pcy; }
        if (e.key === 'ArrowLeft') nx -= step; if (e.key === 'ArrowRight') nx += step;
        if (e.key === 'ArrowUp') ny -= step; if (e.key === 'ArrowDown') ny += step;
        if (isGroup) { dispatch({ type: 'SET_DISC_POSITION', kind: 'group', gi: selection.gi, x: nx, y: ny }); }
        else {
          const pang = (Math.atan2(ny - ge.gcy, nx - ge.gcx) * 180) / Math.PI;
          const pR = Math.hypot(nx - ge.gcx, ny - ge.gcy);
          dispatch({ type: 'SET_DISC_POSITION', kind: 'prog', gi: selection.gi, pi: selection.pi, pang, pR });
        }
      } else if (e.key === '-' || e.key === '=' || e.key === '+') {
        e.preventDefault();
        const delta = e.key === '-' ? -8 : 8;
        if (isGroup) { dispatch({ type: 'SET_SIZE', target: 'gdisc', gi: selection.gi, value: ge.discR + delta }); }
        else { const pe = ge.progs[selection.pi]; dispatch({ type: 'SET_SIZE', target: 'pdisc', gi: selection.gi, pi: selection.pi, value: pe.discR + delta }); }
      } else if (e.key === '[' || e.key === ']') {
        e.preventDefault();
        const delta = e.key === '[' ? -8 : 8;
        if (isGroup && ge.solo) { dispatch({ type: 'SET_SIZE', target: 'gring', gi: selection.gi, value: ge.progs[0].ring + delta, min: 40, max: 520 }); }
        else if (!isGroup) { const pe = ge.progs[selection.pi]; dispatch({ type: 'SET_SIZE', target: 'pring', gi: selection.gi, pi: selection.pi, value: pe.ring + delta, min: 34, max: 460 }); }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dispatch, selection, editMode, layout, sunX, sunY, sunR, onActivate]);
}
